'use client';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import { ResponsiveImage } from '@web/molecules';
import {
  StyledValuesBanner,
  ScrollContainer,
  MarqueeOne,
  MarqueeTwo,
  ValuesWrapper,
  ValueList,
  Value,
  IconWrapper,
  Text,
} from './styles.js';

const VALUE_ICON_SIZE = 24;

export const ValuesBanner = ({
  values,
  backgroundColor = 'background.dark',
}) => {
  const containerRef = useRef(null);
  const marquee1Ref = useRef(null);

  const [repeat, setRepeat] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClickPaused, setIsClickPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isAnimationPaused = isHovered || isClickPaused || prefersReducedMotion;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = e => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!isClickPaused) return;

    const resume = () => setIsClickPaused(false);

    // Defer so the click/tap that paused does not immediately resume.
    const frameId = requestAnimationFrame(() => {
      document.addEventListener('click', resume);
      document.addEventListener('touchstart', resume);
    });

    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener('click', resume);
      document.removeEventListener('touchstart', resume);
    };
  }, [isClickPaused]);

  const handlePauseInteraction = e => {
    e.stopPropagation();
    setIsClickPaused(true);
  };

  useEffect(() => {
    const onViewportChange = () => {
      if (!marquee1Ref.current) return;
      const { width } = marquee1Ref.current.getBoundingClientRect();
      if (!width || width < 0) {
        return;
      }
      const innerWidth = window.innerWidth;
      const repeat = Math.ceil(innerWidth / width);
      setRepeat(repeat);

      setTimeout(() => {
        setLoaded(true);
      }, 250);
    };

    window.addEventListener('resize', onViewportChange);
    onViewportChange();
    return () => {
      window.removeEventListener('resize', onViewportChange);
    };
  }, [values]);

  const content = (
    <>
      {range(repeat).map((e, i) => (
        <ValuesWrapper key={i}>
          <ValueList>
            {values.map(({ image, name, fontColor }) => (
              <Value key={name} fontColor={fontColor || 'text.lightest'}>
                {image && (
                  <IconWrapper>
                    <ResponsiveImage
                      src={image.src}
                      alt={image.alt}
                      width={VALUE_ICON_SIZE}
                      height={VALUE_ICON_SIZE}
                      objectFit="contain"
                    />
                  </IconWrapper>
                )}
                <Text>{name}</Text>
              </Value>
            ))}
          </ValueList>
        </ValuesWrapper>
      ))}
    </>
  );

  return (
    <StyledValuesBanner
      ref={containerRef}
      backgroundColor={backgroundColor}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handlePauseInteraction}
      onTouchStart={handlePauseInteraction}
    >
      <ScrollContainer>
        <MarqueeOne
          ref={marquee1Ref}
          $loaded={loaded}
          $paused={isAnimationPaused}
        >
          {content}
        </MarqueeOne>
        <MarqueeTwo $loaded={loaded} $paused={isAnimationPaused}>
          {content}
        </MarqueeTwo>
      </ScrollContainer>
    </StyledValuesBanner>
  );
};

ValuesBanner.displayName = 'ValuesBanner';

ValuesBanner.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.shape({
        src: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
          .isRequired,
        alt: PropTypes.string.isRequired,
        fontColor: PropTypes.string,
      }),
    }),
  ).isRequired,
};
